import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  collection,
  collectionChanges,
  Firestore,
  orderBy,
  query,
  where,
} from '@angular/fire/firestore';
import { environment } from '@envs/environment';
import { LocationsService } from '@core/services';
import { ToasterService } from '@shared/services';
import { API_ENDPOINTS } from '@shared/constants';
import { combineLatest, switchMap, take, tap } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

interface IAlert {
  id: number;
  type: string;
  title: string;
  driverId: number;
  locationId: number;
  description: string;
  isAlertOn: boolean;
  status: string;
}

@Injectable()
export class AlertsService {
  readonly #http = inject(HttpClient);
  readonly #firestore = inject(Firestore);
  readonly #locationsService = inject(LocationsService);
  readonly #toasterService = inject(ToasterService);
  readonly #router = inject(Router);
  readonly #recentAlerts = new Set<number>();
  #alertAudio?: HTMLAudioElement;

  initializeAlerts() {
    return this.getDriverAlerts().pipe(
      tap(alerts => {
        alerts.forEach(alert => {
          if (!this.#recentAlerts.has(alert.id)) {
            this._playAlertSound();
            this.#recentAlerts.add(alert.id);
            this.#toasterService.showWarning(alert.description, {
              id: alert.id,
              summary: 'Driver emergency alert',
              data: {
                clickCb: () => {
                  this.#router.navigate(['/drivers/alerts']);
                  this.#alertAudio?.pause();
                  this.handleDriverAlertStatus(alert.locationId);
                },
              },
            });
          }
        });
      })
    );
  }

  private handleDriverAlertStatus(locationId: number) {
    return this.updateDriverAlertStatus(locationId).subscribe(() =>
      this.#toasterService.clearMessage()
    );
  }

  private getDriverAlerts() {
    const collectionRef = this.createCollectionRef();
    return this.#locationsService.selectedLocationsIds$.pipe(
      switchMap(selectedLocations =>
        this.createCombinedQueries(collectionRef, selectedLocations)
      )
    );
  }

  private createCollectionRef() {
    const collectionPath = [
      ...environment.firebaseCollectionName,
      'notifications',
      'dispatchers',
      'emergency-alerts',
    ];
    return collection(this.#firestore, collectionPath.join('/'));
  }

  private createCombinedQueries(
    collectionRef: any,
    selectedLocations: number[]
  ) {
    const locationsChunks = this.chunkArray(selectedLocations, 10);
    const queries$ = locationsChunks.map(chunk =>
      this.createChunkQuery(collectionRef, chunk)
    );
    return combineLatest(queries$).pipe(map(results => results.flat()));
  }

  private createChunkQuery(collectionRef: any, chunk: number[]) {
    const queryRef = query(
      collectionRef,
      where('isAlertOn', '==', true),
      where('locationId', 'in', chunk),
      orderBy('date', 'desc')
    );

    return collectionChanges(queryRef).pipe(
      startWith([]),
      map(changes => changes.map(change => change.doc.data() as IAlert))
    );
  }

  private updateDriverAlertStatus(locationId: number) {
    return this.#http
      .put(
        `${API_ENDPOINTS.DriverAlerts.UpdateDriverAlertStatusOnFireStore}?locationId=${locationId}`,
        null
      )
      .pipe(take(1));
  }

  private _playAlertSound() {
    if (!this.#alertAudio) {
      this.#alertAudio = new Audio('/assets/media/danger_alert.wav');
    }
    if (this.#alertAudio.paused) {
      this.#alertAudio.play().catch(error => {
        console.error('Error playing alert sound:', error);
      });
    } else {
      this.#alertAudio.currentTime = 0; // Reset to start
    }
  }

  private chunkArray<T>(array: T[], size = 10): T[][] {
    const chunked = [];
    for (let i = 0; i < array.length; i += size) {
      chunked.push(array.slice(i, i + size));
    }
    return chunked;
  }
}
